import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  List,
  ListItem,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import DvrOutlinedIcon from "@mui/icons-material/DvrOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { getMisTareasPendientes } from "../../../api/Proyectos/ProyectosTareasApi";
import {
  dashCountChipSx,
  dashListRowSx,
  dashLinkBtnSx,
  dashIconWellSx,
  getDashAccent,
} from "../myDashboardStyles";

function formatFechaLimite(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function tareaDescripcion(tarea) {
  const raw = tarea.Descripcion || "";
  return String(raw).replace(/\s+/g, " ").trim();
}

function isOverdue(fechaLimite, estado) {
  if (!fechaLimite || estado === "Done") return false;
  const d = new Date(fechaLimite);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
}

export default function MyTasksWidget({ editing }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tareas, setTareas] = useState([]);
  const [total, setTotal] = useState(0);
  const accent = getDashAccent("widget-my-tasks", "widgets", theme.palette.mode);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getMisTareasPendientes();
        const list = Array.isArray(data) ? data : [];
        const sorted = [...list].sort((a, b) => {
          const aDate = a.FechaLimite
            ? new Date(a.FechaLimite).getTime()
            : Infinity;
          const bDate = b.FechaLimite
            ? new Date(b.FechaLimite).getTime()
            : Infinity;
          if (aDate !== bDate) return aDate - bDate;
          return Number(b.Id) - Number(a.Id);
        });
        if (!cancelled) {
          setTotal(sorted.length);
          setTareas(sorted);
        }
      } catch {
        if (!cancelled) {
          setTareas([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box
      sx={{ height: "100%", display: "flex", flexDirection: "column", p: 1.75 }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <Box
          sx={(th) => ({
            ...dashIconWellSx(th, accent),
            width: 38,
            height: 38,
            borderRadius: "12px",
          })}
        >
          <DvrOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle2" fontWeight={800} letterSpacing={0.1}>
          {t("myDashboard.widgetMyTasks")}
        </Typography>
        <Box sx={(th) => dashCountChipSx(th, accent)}>{total}</Box>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : tareas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            {t("myDashboard.noPendingTasks")}
          </Typography>
        ) : (
          <List dense disablePadding>
            {tareas.map((tarea) => {
              const descripcion = tareaDescripcion(tarea);
              const fecha = formatFechaLimite(tarea.FechaLimite);
              const overdue = isOverdue(tarea.FechaLimite, tarea.Estado);
              const marca =
                tarea.MarcaNombre ||
                t("myDashboard.taskNoBrand", { defaultValue: "Sin marca" });

              return (
                <ListItem
                  key={tarea.Id || tarea.id}
                  disableGutters
                  sx={(th) => ({
                    ...dashListRowSx(th),
                    cursor: editing ? "default" : "pointer",
                    alignItems: "flex-start",
                    flexDirection: "column",
                    gap: 0.4,
                    py: 1,
                  })}
                  onClick={() => {
                    if (!editing) navigate("/tareas");
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      noWrap
                      sx={{ flex: 1, minWidth: 0, color: accent }}
                    >
                      {marca}
                    </Typography>
                    {tarea.Estado && (
                      <Chip
                        size="small"
                        label={tarea.Estado}
                        sx={{
                          height: 20,
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      width: "100%",
                      color: "text.primary",
                      fontWeight: 600,
                      lineHeight: 1.35,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {descripcion ||
                      t("myDashboard.taskNoDescription", {
                        defaultValue: "Sin descripción",
                      })}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: 0.15,
                    }}
                  >
                    <EventOutlinedIcon
                      sx={{
                        fontSize: 14,
                        color: overdue ? "error.main" : "text.secondary",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: overdue ? 700 : 600,
                        color: overdue ? "error.main" : "text.secondary",
                      }}
                    >
                      {fecha
                        ? `${t("myDashboard.taskDueDate", {
                            defaultValue: "Límite",
                          })}: ${fecha}`
                        : t("myDashboard.taskNoDueDate", {
                            defaultValue: "Sin fecha límite",
                          })}
                    </Typography>
                  </Box>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {!editing && (
        <Button
          size="small"
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => navigate("/tareas")}
          sx={dashLinkBtnSx}
        >
          {t("myDashboard.openTasks")}
        </Button>
      )}
    </Box>
  );
}
